<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="fill-height">
        <v-col cols="12" md="10" lg="8">
          <!-- Evidence Builder Hero Card -->
          <v-card class="mb-6 eb-hero-card" elevation="4">
            <!-- Gradient Header -->
            <div class="eb-hero-header">
              <div class="eb-hero-header-content">
                <div class="d-flex align-center mb-2">
                  <v-avatar size="48" class="mr-3 eb-hero-logo">
                    <v-icon size="28" color="white">mdi-file-plus-outline</v-icon>
                  </v-avatar>
                  <div>
                    <h1 class="eb-hero-title">
                      {{ evidence ? 'Edit Evidence' : 'Create Evidence' }}
                    </h1>
                    <p class="eb-hero-subtitle">Build your compliance documentation</p>
                  </div>
                </div>
              </div>
            </div>

            <v-card-text class="pt-6">
              <p class="text-body-1 mb-5 eb-intro-text">
                Evidence is documentation that supports your security claims. Complete the form
                below to add evidence to your security assurance case.
              </p>

              <!-- Workflow Section -->
              <div class="eb-workflow-section">
                <div class="eb-section-header mb-4">
                  <v-icon color="primary" size="28" class="mr-2">mdi-information-outline</v-icon>
                  <span class="text-h6 font-weight-bold">How It Works</span>
                </div>

                <v-row>
                  <v-col cols="12" md="4">
                    <div class="eb-info-card">
                      <div class="eb-info-icon-wrapper draft-icon">
                        <v-icon size="32" color="white">mdi-pencil-outline</v-icon>
                      </div>
                      <div class="eb-info-title">Draft Status</div>
                      <div class="eb-info-description">
                        Provide only name, description, and type without uploading files to save as
                        <strong>Draft</strong>.
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="eb-info-card">
                      <div class="eb-info-icon-wrapper pending-icon">
                        <v-icon size="32" color="white">mdi-clock-outline</v-icon>
                      </div>
                      <div class="eb-info-title">Pending Review</div>
                      <div class="eb-info-description">
                        Upload files or provide links to mark your submission as
                        <strong>Pending Review</strong>.
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="eb-info-card">
                      <div class="eb-info-icon-wrapper required-icon">
                        <v-icon size="32" color="white">mdi-check-circle-outline</v-icon>
                      </div>
                      <div class="eb-info-title">Required Fields</div>
                      <div class="eb-info-description">
                        <strong>Name</strong>, <strong>Description</strong>, <strong>Type</strong>,
                        and <strong>Source</strong> are required.
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>

          <!-- Form Card -->
          <v-card class="eb-form-card" elevation="3">
            <div class="eb-form-header">
              <v-icon color="white" class="mr-2">mdi-form-textbox</v-icon>
              <span class="white--text text-h6 font-weight-bold">Evidence Details</span>
            </div>
            <loader :loading="loading"></loader>
            <v-form ref="form" v-model="valid" :lazy-validation="false" class="form-container">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.title"
                    :rules="[rules.required, rules.duplicateName]"
                    :error-messages="
                      duplicateNameExists ? 'Evidence with this name already exists' : ''
                    "
                    label="Name"
                    outlined
                    :dense="false"
                    required
                    class="modern-input"
                    @input="debouncedCheckDuplicate"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Mapped SubClaim Display (Read-only) -->
              <v-row v-if="mappedSubClaim">
                <v-col cols="12">
                  <v-alert type="info" prominent border="left" class="mapped-subclaim-alert">
                    <v-row align="center">
                      <v-col class="grow">
                        <div class="text-subtitle-1 font-weight-bold mb-1">
                          {{ $t('evidence.mappedToSubClaim') }}
                        </div>
                        <div class="text-body-2">
                          <strong>{{ $t('subClaims.headers.title') }}:</strong>
                          {{ mappedSubClaim.title || 'N/A' }}
                        </div>
                        <div class="text-body-2 mt-1">
                          <strong>{{ $t('subClaims.headers.description') }}:</strong>
                          {{ mappedSubClaim.description || 'N/A' }}
                        </div>
                        <div class="text-caption mt-2 grey--text">
                          <strong>{{ $t('subClaims.headers.id') }}:</strong> {{ mappedSubClaim.id }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-alert>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    :label="$tc('evidence.headers.description', 2)"
                    :hint="$tc('evidence.evidenceDescriptionHint')"
                    auto-grow
                    rows="2"
                    max-rows="6"
                    outlined
                    :dense="false"
                    :rules="[rules.required]"
                    required
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-combobox
                    clearable
                    v-model="form.type"
                    :label="$tc('evidence.type.label', 2)"
                    :items="[
                      $tc('evidence.type.screenshots'),
                      $tc('evidence.type.logs'),
                      $tc('evidence.type.policyDocuments'),
                      $tc('evidence.type.procedureManuals'),
                      $tc('evidence.type.restResults'),
                    ]"
                    outlined
                    :rules="[rules.required]"
                    required
                    class="modern-input"
                  ></v-combobox>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="form.source"
                    :items="['Manual', 'Threat Spider', 'Dependency Track', 'Threat Dragon JSON']"
                    label="Evidence Source"
                    outlined
                    :rules="[rules.required]"
                    required
                    class="modern-input"
                    hint="Select the source of this evidence"
                    persistent-hint
                  ></v-select>
                </v-col>
              </v-row>

              <v-row v-if="form.type && form.source">
                <v-col cols="12">
                  <v-text-field
                    v-if="isToolType"
                    v-model="urlEndpoint"
                    label="Provide URL Endpoint"
                    placeholder="Enter the URL endpoint for your tool integration"
                    outlined
                    :dense="false"
                    :rules="[rules.required]"
                    required
                    class="modern-input"
                    hint="Provide the endpoint URL for your tool integration"
                    persistent-hint
                  ></v-text-field>
                  <v-file-input
                    v-else
                    v-model="file"
                    :label="
                      this.form.fileName && this.form.fileName !== 'null'
                        ? this.form.fileName
                        : `Upload ${form.type || 'File'}`
                    "
                    outlined
                    :dense="false"
                    required
                    class="modern-input"
                  ></v-file-input>
                </v-col>
              </v-row>

              <!-- Additional Information Fields Section -->
              <v-row
                v-if="
                  form.type &&
                    form.source &&
                    (file || urlEndpoint || (evidence && form.status !== 'Draft'))
                "
              >
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <div class="text-h6 mb-3 d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-information-variant</v-icon>
                    Additional Information
                  </div>
                  <!-- Quality Indicators Guide -->
                  <div class="eb-section-header mb-2">
                    <v-icon color="primary" size="20" class="mr-2">mdi-percent</v-icon>
                    <span class="text-subtitle-2 font-weight-bold"
                      >Evidence Quality Indicators (0–100%)</span
                    >
                  </div>
                  <v-row class="mb-2">
                    <v-col cols="12" sm="6" md="3">
                      <div class="eb-pct-guide-card quality-guide">
                        <v-icon size="22" color="amber darken-2" class="mb-1">mdi-star</v-icon>
                        <div class="eb-pct-guide-title">Quality</div>
                        <div class="eb-pct-guide-desc">
                          How good is this evidence? <strong>Higher = better.</strong> 90% →
                          <em>Excellent</em>. 30% → <em>Low quality</em>.
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="eb-pct-guide-card provenance-guide">
                        <v-icon size="22" color="blue darken-2" class="mb-1"
                          >mdi-source-branch</v-icon
                        >
                        <div class="eb-pct-guide-title">Provenance</div>
                        <div class="eb-pct-guide-desc">
                          How trustworthy is the origin? <strong>Higher = more trusted.</strong> 90%
                          → <em>Highly Trusted</em>. 30% → <em>Questionable</em>.
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="eb-pct-guide-card uncertainty-guide">
                        <v-icon size="22" color="deep-orange darken-1" class="mb-1"
                          >mdi-help-circle</v-icon
                        >
                        <div class="eb-pct-guide-title">Uncertainty</div>
                        <div class="eb-pct-guide-desc">
                          How uncertain is the evidence? <strong>Lower = better.</strong> 10% →
                          <em>Very certain</em>. 80% → <em>Highly uncertain</em>.
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="eb-pct-guide-card assumption-guide">
                        <v-icon size="22" color="purple darken-1" class="mb-1"
                          >mdi-thought-bubble</v-icon
                        >
                        <div class="eb-pct-guide-title">Assumption</div>
                        <div class="eb-pct-guide-desc">
                          How many assumptions were made? <strong>Lower = better.</strong> 10% →
                          <em>Few assumptions</em>. 80% → <em>Heavily assumed</em>.
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider class="mb-4"></v-divider>
                </v-col>

                <!-- Quality -->
                <v-col cols="12" md="6">
                  <div class="pct-field">
                    <div class="pct-field-header">
                      <v-icon small color="amber darken-2" class="mr-1">mdi-star</v-icon>
                      <span class="pct-field-label">Quality</span>
                      <v-chip
                        x-small
                        dark
                        class="ml-auto pct-chip"
                        :color="qualityMeta(form.quality).color"
                      >
                        {{ form.quality != null ? form.quality + '%' : '—' }}
                        <span v-if="form.quality != null">
                          — {{ qualityMeta(form.quality).label }}</span
                        >
                      </v-chip>
                    </div>
                    <v-slider
                      v-model="form.quality"
                      min="0"
                      max="100"
                      step="1"
                      :color="qualityMeta(form.quality).color"
                      track-color="grey lighten-2"
                      thumb-label
                      class="pct-slider"
                    ></v-slider>
                    <div class="pct-hint">Higher % = better quality evidence</div>
                    <v-divider class="pct-notes-divider"></v-divider>
                    <v-textarea
                      v-model="form.qualityNote"
                      placeholder="Add a note about quality… (optional)"
                      auto-grow
                      rows="1"
                      flat
                      solo
                      hide-details
                      class="pct-notes-textarea"
                    ></v-textarea>
                  </div>
                </v-col>

                <!-- Provenance -->
                <v-col cols="12" md="6">
                  <div class="pct-field">
                    <div class="pct-field-header">
                      <v-icon small color="blue darken-2" class="mr-1">mdi-source-branch</v-icon>
                      <span class="pct-field-label">Provenance</span>
                      <v-chip
                        x-small
                        dark
                        class="ml-auto pct-chip"
                        :color="provenanceMeta(form.provenance).color"
                      >
                        {{ form.provenance != null ? form.provenance + '%' : '—' }}
                        <span v-if="form.provenance != null">
                          — {{ provenanceMeta(form.provenance).label }}</span
                        >
                      </v-chip>
                    </div>
                    <v-slider
                      v-model="form.provenance"
                      min="0"
                      max="100"
                      step="1"
                      :color="provenanceMeta(form.provenance).color"
                      track-color="grey lighten-2"
                      thumb-label
                      class="pct-slider"
                    ></v-slider>
                    <div class="pct-hint">Higher % = more trustworthy origin/source</div>
                    <v-divider class="pct-notes-divider"></v-divider>
                    <v-textarea
                      v-model="form.provenanceNote"
                      placeholder="Add a note about provenance… (optional)"
                      auto-grow
                      rows="1"
                      flat
                      solo
                      hide-details
                      class="pct-notes-textarea"
                    ></v-textarea>
                  </div>
                </v-col>

                <!-- Uncertainty -->
                <v-col cols="12" md="6">
                  <div class="pct-field">
                    <div class="pct-field-header">
                      <v-icon small color="deep-orange darken-1" class="mr-1"
                        >mdi-help-circle</v-icon
                      >
                      <span class="pct-field-label">Uncertainty</span>
                      <v-chip
                        x-small
                        dark
                        class="ml-auto pct-chip"
                        :color="uncertaintyMeta(form.uncertainty).color"
                      >
                        {{ form.uncertainty != null ? form.uncertainty + '%' : '—' }}
                        <span v-if="form.uncertainty != null">
                          — {{ uncertaintyMeta(form.uncertainty).label }}</span
                        >
                      </v-chip>
                    </div>
                    <v-slider
                      v-model="form.uncertainty"
                      min="0"
                      max="100"
                      step="1"
                      :color="uncertaintyMeta(form.uncertainty).color"
                      track-color="grey lighten-2"
                      thumb-label
                      class="pct-slider"
                    ></v-slider>
                    <div class="pct-hint">Lower % is better — less uncertainty in the evidence</div>
                    <v-divider class="pct-notes-divider"></v-divider>
                    <v-textarea
                      v-model="form.uncertaintyNote"
                      placeholder="Add a note about uncertainty… (optional)"
                      auto-grow
                      rows="1"
                      flat
                      solo
                      hide-details
                      class="pct-notes-textarea"
                    ></v-textarea>
                  </div>
                </v-col>

                <!-- Assumption -->
                <v-col cols="12" md="6">
                  <div class="pct-field">
                    <div class="pct-field-header">
                      <v-icon small color="purple darken-1" class="mr-1">mdi-thought-bubble</v-icon>
                      <span class="pct-field-label">Assumption</span>
                      <v-chip
                        x-small
                        dark
                        class="ml-auto pct-chip"
                        :color="assumptionMeta(form.assumption).color"
                      >
                        {{ form.assumption != null ? form.assumption + '%' : '—' }}
                        <span v-if="form.assumption != null">
                          — {{ assumptionMeta(form.assumption).label }}</span
                        >
                      </v-chip>
                    </div>
                    <v-slider
                      v-model="form.assumption"
                      min="0"
                      max="100"
                      step="1"
                      :color="assumptionMeta(form.assumption).color"
                      track-color="grey lighten-2"
                      thumb-label
                      class="pct-slider"
                    ></v-slider>
                    <div class="pct-hint">Lower % is better — fewer assumptions made</div>
                    <v-divider class="pct-notes-divider"></v-divider>
                    <v-textarea
                      v-model="form.assumptionNote"
                      placeholder="Add a note about assumptions… (optional)"
                      auto-grow
                      rows="1"
                      flat
                      solo
                      hide-details
                      class="pct-notes-textarea"
                    ></v-textarea>
                  </div>
                </v-col>
              </v-row>

              <!-- SoC Mapping Section -->
              <v-row>
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <div class="text-h6 mb-4 d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-certificate</v-icon>
                    Mapped Subject of Certification (Optional)
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="selectedSocIds"
                    :items="socList"
                    item-text="name"
                    item-value="id"
                    label="Select Subject(s) of Certification"
                    outlined
                    multiple
                    chips
                    deletable-chips
                    :dense="false"
                    class="modern-input"
                    hint="Select one or more SoCs this evidence relates to (optional)"
                    persistent-hint
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 3"
                        close
                        @click:close="removeSoc(item.id)"
                        color="primary"
                        outlined
                        small
                      >
                        {{ item.name }}
                      </v-chip>
                      <span v-if="index === 3" class="grey--text text-caption">
                        (+{{ selectedSocIds.length - 3 }} others)
                      </span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ item.standardFamily }} - {{ item.type }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:prepend-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="text-subtitle-2 grey--text">
                            {{ selectedSocIds.length }} SoC(s) selected
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row class="button-row">
                <v-spacer></v-spacer>
                <v-btn class="action-btn cancel-btn" @click="cancel">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn class="action-btn reset-btn" @click="resetForm">
                  <v-icon left>mdi-refresh</v-icon>{{ $t('reset') }}
                </v-btn>
                <v-btn
                  class="action-btn submit-btn"
                  color="light-blue"
                  :disabled="!valid"
                  @click="handleEvidenceUpload()"
                >
                  <v-icon left>mdi-upload</v-icon
                  >{{ $t(this.evidence ? 'update' : 'evidence.create') }}
                </v-btn>
              </v-row>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import Loader from '@/components/helpers/Loader';
import util from '@/mixins/util.js';
import evidenceApi from '../api/evidenceApi';
import EventBus from '@/helpers/event-bus.js';
import { debounce } from 'lodash';
import Soc from '@/modules/soc/api/soc.js';

export default {
  name: 'ModelUploadBuilder',
  mixins: [util],
  components: {
    Loader,
  },
  data() {
    return {
      evidence: localStorage.getItem('evidence'),
      valid: false,
      loading: false,
      form: {
        name: '',
        description: '',
        type: '',
        source: '',
        fileName: null,
        uncertainty: null,
        quality: null,
        provenance: null,
        assumption: null,
        qualityNote: '',
        provenanceNote: '',
        uncertaintyNote: '',
        assumptionNote: '',
      },
      file: null,
      urlEndpoint: '',
      duplicateNameExists: false,
      checkingDuplicate: false,
      mappedSubClaim: null,
      selectedSocIds: [],
      socList: [],
      rules: {
        required: value => !!value || 'Required.',
        duplicateName: () => !this.duplicateNameExists || 'Evidence with this name already exists.',
      },
    };
  },
  computed: {
    isToolType() {
      return (
        this.form.source === 'Dependency Track' ||
        this.form.source === 'Threat Spider' ||
        this.form.source === 'Threat Dragon JSON'
      );
    },
    // Percentage helpers
    qualityMeta() {
      return v => {
        if (v == null) return { label: 'Not set', color: 'grey' };
        if (v >= 86) return { label: 'Excellent', color: 'green darken-2' };
        if (v >= 61) return { label: 'High', color: 'light-green darken-1' };
        if (v >= 31) return { label: 'Medium', color: 'amber darken-2' };
        return { label: 'Low', color: 'red darken-1' };
      };
    },
    provenanceMeta() {
      return v => {
        if (v == null) return { label: 'Not set', color: 'grey' };
        if (v >= 86) return { label: 'Highly Trusted', color: 'green darken-2' };
        if (v >= 61) return { label: 'Trusted', color: 'light-green darken-1' };
        if (v >= 31) return { label: 'Moderate', color: 'amber darken-2' };
        return { label: 'Questionable', color: 'red darken-1' };
      };
    },
    uncertaintyMeta() {
      return v => {
        if (v == null) return { label: 'Not set', color: 'grey' };
        if (v >= 76) return { label: 'Very High', color: 'red darken-2' };
        if (v >= 51) return { label: 'High', color: 'deep-orange darken-1' };
        if (v >= 26) return { label: 'Moderate', color: 'amber darken-2' };
        return { label: 'Low', color: 'green darken-1' };
      };
    },
    assumptionMeta() {
      return v => {
        if (v == null) return { label: 'Not set', color: 'grey' };
        if (v >= 76) return { label: 'Heavily Assumed', color: 'red darken-2' };
        if (v >= 51) return { label: 'Many Assumptions', color: 'deep-orange darken-1' };
        if (v >= 26) return { label: 'Moderate', color: 'amber darken-2' };
        return { label: 'Few Assumptions', color: 'green darken-1' };
      };
    },
  },
  watch: {
    'form.name'(value) {
      if (this.form && !value) this.$refs.form.resetValidation();
    },
    'form.isPublic'(value) {
      if (this.form && !value) this.$refs.form.resetValidation();
    },
    'form.title': {
      handler: function(newVal) {
        if (newVal) {
          this.debouncedCheckDuplicate();
        } else {
          this.duplicateNameExists = false;
        }
      },
    },
    'form.type': function() {
      // Reset the file or URL input when switching between types
      if (this.isToolType) {
        this.file = null;
      } else {
        this.urlEndpoint = '';
      }
    },
  },
  async created() {
    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 500);
    // Fetch SoCs when component is created
    await this.fetchSocs();
  },
  async mounted() {
    if (this.evidence) {
      this.form = JSON.parse(this.evidence);
      console.log('Loaded evidence for editing:', this.form);
      console.log('Available socList:', this.socList);

      // Ensure source is set to 'Manual' if not provided
      if (!this.form.source || this.form.source === 'null' || this.form.source === '') {
        this.form.source = 'Manual';
      }

      // If we're editing a tool-type evidence, populate the URL field
      if (this.isToolType && this.form.fileName) {
        this.urlEndpoint = this.form.fileName;
      }

      // Parse percentage fields (may be stored as strings) into numbers
      ['uncertainty', 'quality', 'provenance', 'assumption'].forEach(field => {
        const v = this.form[field];
        if (v !== null && v !== '' && v !== undefined) {
          const n = Number(v);
          this.form[field] = isNaN(n) ? null : n;
        } else {
          this.form[field] = null;
        }
      });

      // Load mapped SoCs - check socIds property (which is what the evidence actually has)
      if (this.form.socIds && Array.isArray(this.form.socIds) && this.form.socIds.length > 0) {
        console.log('Found socIds in form:', this.form.socIds);

        // Extract IDs and convert to strings to match socList format
        this.selectedSocIds = this.form.socIds.map(socIdObj => {
          // socIds could be an array of objects like [{id: "1", name: "..."}, ...]
          // or simple IDs like ["1", "2", ...]
          const id = typeof socIdObj === 'object' && socIdObj.id ? socIdObj.id : socIdObj;
          const stringId = String(id);
          console.log('Processing SoC ID:', socIdObj, '-> String ID:', stringId);
          return stringId;
        });

        console.log('Selected SoC IDs from socIds array:', this.selectedSocIds);
        console.log(
          'Available SoC IDs in list:',
          this.socList.map(s => String(s.id)),
        );
      } else {
        console.log('No socIds found in form or empty array');
      }
    }

    // Load mapped subclaim info if coming from SubClaimEvidenceMapper
    const subClaimData = localStorage.getItem('subClaim');
    if (subClaimData) {
      try {
        this.mappedSubClaim = JSON.parse(subClaimData);
      } catch (e) {
        console.error('Error parsing subclaim data:', e);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    localStorage.removeItem('evidence');
    next();
  },
  methods: {
    async checkDuplicateName() {
      if (!this.form.title || this.form.title.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }

      // Don't check if we're editing an existing evidence with the same name
      if (this.evidence) {
        const currentEvidence = JSON.parse(this.evidence);
        if (currentEvidence.title === this.form.title) {
          this.duplicateNameExists = false;
          return;
        }
      }

      this.checkingDuplicate = true;
      try {
        const response = await evidenceApi.checkDuplicateName(this.form.title);
        // The API returns a boolean directly, not an object with exists property
        this.duplicateNameExists = response.data;

        // Force form validation to update immediately
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      } catch (error) {
        console.error('Error checking duplicate name:', error);
        this.duplicateNameExists = false;
      } finally {
        this.checkingDuplicate = false;
      }
    },
    resetForm() {
      this.form.title = '';
      this.form.description = '';
      this.form.type = '';
      this.form.uncertainty = null;
      this.form.quality = null;
      this.form.provenance = null;
      this.form.assumption = null;
      this.form.qualityNote = '';
      this.form.provenanceNote = '';
      this.form.uncertaintyNote = '';
      this.form.assumptionNote = '';
      this.file = null;
      this.urlEndpoint = '';
      this.duplicateNameExists = false;
      this.selectedSocIds = [];
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },
    handleEvidenceUpload() {
      // Add check to prevent proceeding if duplicate name exists
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'Evidence with this name already exists',
          color: 'error',
        });
        return;
      }

      if (this.evidence) {
        this.uploadEvidence();
      } else {
        this.createEvidence();
      }
    },
    cancel() {
      if (localStorage.getItem('subClaim') !== null) {
        this.$router.push({ name: 'SubClaimEvidenceMapper' });
      } else {
        this.$router.push({ name: 'EvidenceList' });
      }
    },
    async fetchSocs() {
      try {
        const response = await Soc.all();
        this.socList = response.data || [];
      } catch (error) {
        console.error('Error fetching SoCs:', error);
        EventBus.$emit('snackbar', {
          text: 'Error loading Subjects of Certification',
          color: 'error',
        });
      }
    },
    removeSoc(socId) {
      const index = this.selectedSocIds.indexOf(socId);
      if (index > -1) {
        this.selectedSocIds.splice(index, 1);
      }
    },
    async createEvidence() {
      // Double-check for duplicates before proceeding
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'Evidence with this name already exists',
          color: 'error',
        });
        return;
      }

      const formData = new FormData();

      // Handle different evidence sources differently
      if (this.isToolType) {
        // For tool types, use the URL as the fileName
        formData.append('fileName', this.urlEndpoint || 'null');
        // No file to upload for tool types
      } else if (this.file !== null) {
        formData.append('file', this.file);
        formData.append('fileName', this.file.name);
      } else {
        // Always append fileName parameter even when no file is provided
        formData.append('fileName', 'null');
      }

      formData.append('title', this.form.title);
      formData.append('description', this.form.description);
      formData.append('type', this.form.type);
      formData.append('source', this.form.source || 'Manual');
      formData.append('version', '1.0');

      // Append new fields (only if they have values)
      formData.append('uncertainty', this.form.uncertainty != null ? this.form.uncertainty : '');
      formData.append('quality', this.form.quality != null ? this.form.quality : '');
      formData.append('provenance', this.form.provenance != null ? this.form.provenance : '');
      formData.append('assumption', this.form.assumption != null ? this.form.assumption : '');
      formData.append('qualityNote', this.form.qualityNote || '');
      formData.append('provenanceNote', this.form.provenanceNote || '');
      formData.append('uncertaintyNote', this.form.uncertaintyNote || '');
      formData.append('assumptionNote', this.form.assumptionNote || '');

      // Append SoC IDs
      if (this.selectedSocIds && this.selectedSocIds.length > 0) {
        formData.append('socIds', this.selectedSocIds.join(','));
      }

      if (localStorage.getItem('subClaim'))
        formData.append('subClaim', localStorage.getItem('subClaim'));
      this.loading = true;

      try {
        await evidenceApi.create(formData);
        if (localStorage.getItem('subClaim') !== null) {
          this.$router.push({ name: 'SubClaimEvidenceMapper' });
        } else {
          this.$router.push({ name: 'EvidenceList' });
        }
      } catch (error) {
        console.error('Upload error:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },
    async uploadEvidence() {
      // For uploads, only check if we're changing the name
      if (this.evidence) {
        const currentEvidence = JSON.parse(this.evidence);
        if (currentEvidence.title !== this.form.title && this.duplicateNameExists) {
          EventBus.$emit('snackbar', {
            text: 'Evidence with this name already exists',
            color: 'error',
          });
          return;
        }
      }

      const formData = new FormData();
      formData.append('id', this.form.id);

      // Handle different evidence sources differently
      if (this.isToolType) {
        // For tool types, use the URL as the fileName
        formData.append('fileName', this.urlEndpoint || 'null');
        // No file to upload for tool types
      } else if (this.file !== null) {
        formData.append('file', this.file);
        formData.append('fileName', this.file.name);
      } else {
        // Make sure we send the existing fileName or 'null' if it doesn't exist
        formData.append('fileName', this.form.fileName || 'null');
      }

      formData.append('title', this.form.title);
      formData.append('description', this.form.description);
      formData.append('type', this.form.type);
      formData.append('source', this.form.source || 'Manual');
      formData.append('version', '1.0');

      // Append new fields
      formData.append('uncertainty', this.form.uncertainty != null ? this.form.uncertainty : '');
      formData.append('quality', this.form.quality != null ? this.form.quality : '');
      formData.append('provenance', this.form.provenance != null ? this.form.provenance : '');
      formData.append('assumption', this.form.assumption != null ? this.form.assumption : '');
      formData.append('qualityNote', this.form.qualityNote || '');
      formData.append('provenanceNote', this.form.provenanceNote || '');
      formData.append('uncertaintyNote', this.form.uncertaintyNote || '');
      formData.append('assumptionNote', this.form.assumptionNote || '');

      // Append SoC IDs
      if (this.selectedSocIds && this.selectedSocIds.length > 0) {
        formData.append('socIds', this.selectedSocIds.join(','));
      }

      this.loading = true;

      try {
        await evidenceApi.update(formData);
        EventBus.$emit('snackbar', {
          text: this.$t('Updated successfully'),
          color: 'green',
        });
        this.$router.push({ name: 'EvidenceList' });
      } catch (error) {
        console.error('Upload error:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchMappedSubClaim(subClaimId) {
      try {
        const response = await evidenceApi.getSubClaimById(subClaimId);
        this.mappedSubClaim = response.data;
      } catch (error) {
        console.error('Error fetching mapped subclaim:', error);
        this.mappedSubClaim = null;
      }
    },
  },
};
</script>

<style>
/* ============================================
   EVIDENCE BUILDER - Modern Design System
   Matching DashboardContent.vue & SBOM patterns
   ============================================ */

/* Base Styles */
.v-application .info {
  background-color: #00405e !important;
  border-color: #058bf5 !important;
}

/* ============================================
   HERO CARD
   ============================================ */
.eb-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.eb-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.eb-hero-header-content {
  flex: 1;
}

.eb-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.eb-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.eb-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.eb-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.05rem;
}

/* ============================================
   WORKFLOW / INFO SECTION
   ============================================ */
.eb-workflow-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 28px;
  margin-top: 8px;
}

.eb-section-header {
  display: flex;
  align-items: center;
}

.eb-info-card {
  background: white;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.eb-info-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.eb-info-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.eb-info-icon-wrapper.draft-icon {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.3);
}

.eb-info-icon-wrapper.pending-icon {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
}

.eb-info-icon-wrapper.required-icon {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 8px 24px rgba(46, 125, 50, 0.3);
}

.eb-info-card:hover .eb-info-icon-wrapper {
  transform: scale(1.05);
}

.eb-info-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 12px;
  line-height: 1.3;
}

.eb-info-description {
  color: #616161;
  font-size: 0.875rem;
  line-height: 1.6;
  flex-grow: 1;
}

/* ============================================
   FORM CARD
   ============================================ */
.eb-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.eb-form-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.form-container {
  padding: 28px;
}

.modern-input {
  margin-bottom: 16px !important;
}

.modern-input >>> .v-input__control .v-input__slot {
  min-height: 50px !important;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.23);
  transition: border-color 0.2s;
}

.modern-input >>> .v-input__control .v-input__slot:hover,
.modern-input >>> .v-input__control .v-input__slot:focus-within {
  border-color: #1976d2;
}

.modern-input >>> .v-text-field__slot input,
.modern-input >>> .v-text-field__slot textarea {
  padding: 8px 0;
}

/* ============================================
   BUTTONS
   ============================================ */
.button-row {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.action-btn {
  height: 44px;
  margin-left: 12px;
  min-width: 120px;
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  letter-spacing: 0.5px;
  transition: all 0.3s ease !important;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.cancel-btn {
  background-color: #f5f5f5 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.reset-btn {
  background-color: #e0e0e0 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.submit-btn {
  color: white !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

/* ============================================
   MAPPED SUBCLAIM ALERT
   ============================================ */
.mapped-subclaim-alert {
  margin-bottom: 20px !important;
  border-left-width: 6px !important;
  border-radius: 8px !important;
}

.mapped-subclaim-alert .v-alert__content {
  padding: 4px 0;
}

.mapped-subclaim-alert .text-subtitle-1 {
  color: #0080ff;
}

.mapped-subclaim-alert .text-body-2 {
  line-height: 1.6;
}

/* ============================================
   PERCENTAGE SLIDER FIELDS
   ============================================ */
.pct-field {
  background: #fafafa;
  border: 1px solid #e8eaf6;
  border-radius: 10px;
  padding: 14px 16px 8px;
  transition: border-color 0.2s;
}

.pct-field:hover {
  border-color: #9fa8da;
}

.pct-field-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.pct-field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #37474f;
}

.pct-chip {
  font-size: 0.7rem !important;
  font-weight: 700 !important;
}

.pct-slider {
  margin-bottom: 0 !important;
}

.pct-hint {
  font-size: 0.72rem;
  color: #9e9e9e;
  margin-top: -8px;
  padding-bottom: 4px;
}

.pct-notes-divider {
  margin: 10px 0 4px;
  opacity: 0.5;
}

.pct-notes-textarea {
  margin-top: 2px !important;
  font-size: 0.82rem !important;
}

.pct-notes-textarea >>> .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-height: 28px !important;
}

.pct-notes-textarea >>> textarea {
  font-size: 0.82rem !important;
  color: #546e7a !important;
  line-height: 1.5 !important;
}

/* ============================================
   PERCENTAGE GUIDE CARDS
   ============================================ */
.eb-pct-guide-card {
  background: white;
  border-radius: 10px;
  padding: 14px 14px 12px;
  text-align: center;
  height: 100%;
  border: 1.5px solid #e0e0e0;
  transition: all 0.2s ease;
}

.quality-guide {
  border-top: 3px solid #fbc02d;
}
.provenance-guide {
  border-top: 3px solid #1976d2;
}
.uncertainty-guide {
  border-top: 3px solid #e64a19;
}
.assumption-guide {
  border-top: 3px solid #7b1fa2;
}

.eb-pct-guide-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1a237e;
  margin: 6px 0 6px;
}

.eb-pct-guide-desc {
  font-size: 0.75rem;
  color: #616161;
  line-height: 1.5;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .eb-hero-header {
    flex-direction: column;
    text-align: center;
  }

  .eb-hero-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .eb-info-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 600px) {
  .eb-hero-title {
    font-size: 1.4rem;
  }

  .eb-workflow-section {
    padding: 20px 16px;
  }

  .action-btn {
    min-width: 100px;
    margin-left: 8px;
  }
}
</style>
